import { parse } from 'url';

import React, { useEffect, useContext } from 'react';
import type { FC } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { isMobileApp_DO_NOT_USE } from '@confluence/mobile-app-detection-common';

import { getStreamInject } from '@confluence/ssr-data-streaming/entry-points/server';
import { fg } from '@confluence/feature-gating';

import type { RoutesContextType } from './RoutesContext';
import { RoutesContext } from './RoutesContext';

export type RedirectionProps = {
	href?: string;
	name?: string;
	params?: Parameters<RoutesContextType['toUrl']>[1];
	to?: string;
	usePush?: boolean;
	forceReload?: Parameters<RoutesContextType['push']>[1];
	reason?: string;
	skipRouteChange?: boolean;
	excludedQueryParams?: string[];
};

export const Redirection: FC<RedirectionProps> = ({
	href = null,
	name,
	params,
	to = null,
	usePush = false,
	forceReload = false,
	reason,
	skipRouteChange = false,
	excludedQueryParams,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { push, replace, toUrl, getHash, getQueryParams, getHref, matchRoute } =
		useContext(RoutesContext);

	const getUrl = () => {
		const customPath = href || to;
		let parsedUrl = {};
		if (customPath) {
			parsedUrl = parse(customPath, true);
		}

		const currentQuery = getQueryParams();

		if (excludedQueryParams && fg('confluence_frontend_excluded_query_params_redirect')) {
			excludedQueryParams.forEach((param) => {
				delete currentQuery[param];
			});
		}

		return toUrl(name, {
			hash: getHash(),
			query: currentQuery,
			...params,
			...parsedUrl,
		});
	};

	// When it's in Embedded Confluence we want to disable SSR redirection optimization and let SPA redirection
	// otherwise iframe onload event will be triggered too early and cause Embedded Confluence to display login/storage access page
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && !isEmbeddedConfluence_DO_NOT_USE() && !isMobileApp_DO_NOT_USE()) {
		if (window.__SSR_REACT_STREAMING__) {
			const inject = getStreamInject();

			inject(() => {
				let url = getUrl();
				if (!url.startsWith('http') && !url.startsWith('//')) {
					url = window.location.origin + url;
				}
				return React.createElement('script', {
					dangerouslySetInnerHTML: {
						// We use the origin from client side since we cannot get it during SSR
						__html: `window.location.href='${url}'`,
					},
				});
			});
		} else {
			// This instructs cc-frontend-ssr to throw away the SSR output and write an HTML meta redirect instead
			window['__SSR_REDIRECT__'] = getUrl();
			window['__SSR_REDIRECT_REASON__'] = reason;
		}
	}

	useEffect(() => {
		const oldRoute = matchRoute(getHref());
		const oldRouteName = oldRoute?.name || null;
		const getAnalyticsAttributes = ({ newRouteName }: { newRouteName: string | undefined }) => ({
			oldRouteName,
			newRouteName,

			// It is safe to fire an event with the names of the route params because they are not UGC.
			// They are part of our route definitions. In contrast, the values of the route params are UGC.
			// Additionally, both the names and values of the route query params are UGC because they are
			// read directly from the URL without matching against our route definitions.
			oldRouteParamNames: Object.keys(oldRoute?.params || {}),
			newRouteParamNames: Object.keys(params || {}),

			// Space key related attributes.
			isOldRouteSpaceKeyDefined: !!oldRoute?.params?.spaceKey,
			isNewRouteSpaceKeyDefined: !!params?.spaceKey,
			areSpaceKeysEqual: oldRoute?.params?.spaceKey === params?.spaceKey,
			areSpaceKeysEqualLowercase:
				oldRoute?.params?.spaceKey?.toLowerCase() === params?.spaceKey?.toLowerCase(),
		});

		const doRedirect = () => {
			// Reset the delayed content redirection object to avoid unnecessary redirects
			window['__DELAYED_CONTENT_REDIRECTION__'] = {};

			const gottenUrl = getUrl();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'redirect',
					actionSubject: oldRouteName,
					actionSubjectId: name || matchRoute(gottenUrl)?.name,
					source: oldRouteName,
				},
			}).fire();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirect',
					actionSubject: 'route',
					source: 'Redirection',
					attributes: getAnalyticsAttributes({
						newRouteName: name || matchRoute(gottenUrl)?.name,
					}),
				},
			}).fire();

			// Skip route change by only rewrite URL
			if (skipRouteChange) {
				try {
					window.history.replaceState({}, '', gottenUrl);
					return;
				} catch (e) {
					// If replaceState failed, then run the redirect normally
				}
			}

			(usePush ? push : replace)(gottenUrl, forceReload);
		};

		try {
			doRedirect();
		} catch (error) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirectFailed',
					actionSubject: 'route',
					source: 'Redirection',
					attributes: {
						...getAnalyticsAttributes({ newRouteName: name }),
						errorMessage: error?.message,
					},
				},
			}).fire();

			throw error;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};
